import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnInit,
    Optional,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {
    faAlignRight,
    faArrowDown, faArrows,
    faArrowUp,
    faChevronDown,
    faChevronUp, faDownload,
    faEdit,
    faEllipsisV,
    faEnvelope,
    faExternalLink,
    faFile,
    faImage,
    faPlus,
    faSave,
    faSyncAlt,
    faTimes,
    faTrash,
    faVideo
} from '@fortawesome/pro-solid-svg-icons';
import {ContentCell, ContentCellId, Finding, FindingSeverity} from '@webmate-web-core/shared/types/testmgmt';
import {
    DeviceId,
    FindingId,
    ProjectId,
    ScreenshotId,
    SpecId,
    VideoRecordingId
} from '@webmate-web-core/shared/types/ids';
import {catchError, concatMap, filter, finalize, map, mergeMap, startWith, take, takeUntil, takeWhile, tap, toArray} from 'rxjs/operators';
import {Popover} from 'primeng/popover';
import {TestService} from '@webmate-web-core/shared/services/test.service';
import {forkJoin, from, Observable, of, switchMap, throwError} from 'rxjs';
import {ConsoleInfo, ViewId, WorkbenchViewConfig} from '../../../../workbench/shared/types';
import {ImageService} from '@webmate-web-core/shared/services/image.service';
import {ArtifactService} from '@webmate-web-core/shared/services/artifact.service';
import {WorkbenchLayoutManager} from '../../../../workbench/shared/services/workbench-layout-manager.service';
import {WorkbenchCommandRegistry} from '../../../../workbench/shared/services/workbench-command-registry.service';
import {WorkbenchContextService} from '../../../../workbench/shared/services/workbench-context.service';
import * as _ from 'lodash';
import {WellKnownArtifactTypes} from '@webmate-web-core/shared/types/artifact';
import {VideoRecordingService} from '@webmate-web-core/shared/services/video-recording.service';
import {WorkbenchConsoleService} from '../../../../workbench/shared/services/workbench-console.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {SearchService} from '@webmate-web-core/shared/services/search.service';
import {CENTER_VIEW_CONTAINER_ID} from '../../../../workbench/shared/components/viewcontainers/viewcontainers';
import {
    VideoViewerComponent,
    VideoViewerToolConfig
} from '@webmate-web-core/shared/tools/video-viewer/video-viewer.component';
import {ModalToolContainerService} from '@webmate-web-core/shared/tools/modaltoolcontainer/modaltoolcontainer.service';
import {AssetAndBlobService} from '@webmate-web-core/shared/services/assetAndBlob.service';
import {WmNotificationService} from '@webmate-web-core/shared/components/notification/wm-notification.service';
import {MouseEvent} from 'paper';
import {Menu} from 'primeng/menu';
import {
    PlanningMatrixSelectionToolComponent,
    PlanningMatrixToolConfig
} from '@webmate-web-core/projects/project/auditlab/audits/planningmatrix-selection-tool.component';
import {HydratedAuditTarget} from '@webmate-web-core/shared/services/audit.service';
import {IS_WORKBENCH_CONTEXT} from './workbench-context.token';
import {MenuItem} from 'primeng/api';
import {IconDefinition} from '@fortawesome/fontawesome-svg-core';
import {
    FindingCellInsertService,
    PendingFindingCellInsert
} from '@webmate-web-core/shared/services/finding-cell-insert.service';

export type CellType = 'text' | 'screenshot' | 'videoSnippet' | 'attachment' | 'email';

type ImportedFindingCellType = 'screenshot' | 'videoSnippet' | 'attachment';

const SUPPORTED_VIDEO_UPLOAD_MIME_TYPES = new Set(['video/mp4', 'video/webm']);

@UntilDestroy()
@Component({
    selector: 'wm-finding-finding',
    templateUrl: './finding-finding.component.html',
    styleUrls: ['./finding-finding.component.less']
})
export class FindingFindingComponent implements OnInit, OnChanges {
    private static readonly MAX_ATTACHMENT_SIZE_BYTES = 512 * 1024 * 1024;

    @ViewChild('addSpecPopover', {static: false, read: Popover}) addSpecPopover: Popover;

    @ViewChild('sharePopover') sharePopover: Popover;

    @ViewChild('addCellPopover', {static: false, read: Popover}) addCellPopover: Popover;

    @ViewChild('attachmentFileInput', {static: false}) attachmentFileInput?: ElementRef<HTMLInputElement>;
    @ViewChild('imageFileInput', {static: false}) imageFileInput?: ElementRef<HTMLInputElement>;
    @ViewChild('videoFileInput', {static: false}) videoFileInput?: ElementRef<HTMLInputElement>;

    @Input()
    addSpecsAllowed: boolean = true;

    @Input()
    availableSpecs?: SpecId[];

    // if set, the finding will be seeded with this and only updated when required
    @Input()
    finding?: Finding;

    @Input()
    findingId: FindingId;

    @Input()
    showMeta: boolean = true;

    // Show meta in condensed form. Only active if showMeta is true.
    // metaCollapsed is ignored if true
    @Input()
    condensedMeta: boolean = false;

    destroy$ = new EventEmitter<void>();

    @Input()
    metaCollapsed = true;

    @Input()
    collapsable = false;

    @Input()
    public findingCollapsed = true;

    @Input()
    showInlineAddButtons = false;

    @Output() findingUpdated = new EventEmitter<Finding>();

    editedCell?: ContentCellId;

    tmpCellEditContent: string;

    tmpFindingTitle: string;

    projectId: ProjectId;
    deviceId: DeviceId | undefined;
    activeConsole: ConsoleInfo | undefined;
    selectedCenterTabId?: ViewId;

    readonly isWorkbenchContext: boolean;

    addCellMenuItems: MenuItem[] = [];

    hoveredCellIndex: number | null = null;
    insertAtIndex = 0;

    processingFindingAction: boolean = false;

    screenshotsNames: Record<ScreenshotId, string> = {};
    videoNames: Record<VideoRecordingId, string> = {};
    private videoUrl: string;
    uploadingAttachments = false;

    currentActionDropdown: any[];

    constructor(private testService: TestService,
                private cdRef: ChangeDetectorRef,
                private imageService: ImageService,
                private artifactService: ArtifactService,
                private assetAndBlobService: AssetAndBlobService,
                private videoRecordingService: VideoRecordingService,
                private contextService: WorkbenchContextService,
                private searchService: SearchService,
                private notificationService: WmNotificationService,
                @Optional() private workbenchConsoleService: WorkbenchConsoleService,
                @Optional() private layoutManager: WorkbenchLayoutManager,
                @Optional() private workbenchCommandRegistry: WorkbenchCommandRegistry,
                private toolService: ModalToolContainerService,
                private findingCellInsertService: FindingCellInsertService,
                @Optional() @Inject(IS_WORKBENCH_CONTEXT) isWorkbenchContext: boolean | null) {
        this.isWorkbenchContext = isWorkbenchContext === true;
    }

    ngOnInit() {
        this.contextService.subscribeWorkbenchContext$()
            .pipe(untilDestroyed(this))
            .subscribe(context => {
                this.deviceId = context.deviceContext?.activeDevice;
                this.activeConsole = context.deviceContext?.activeConsole;
                this.projectId = context.project;
                this.rebuildAddCellMenuItems();
        });
        if (this.isWorkbenchContext && this.layoutManager) {
            this.layoutManager.subscribeToNormalViewContainerChanges$(CENTER_VIEW_CONTAINER_ID)
                .pipe(untilDestroyed(this))
                .subscribe(state => {
                    this.selectedCenterTabId = state.activeTab;
                    this.rebuildAddCellMenuItems();
                });
        }
        this.rebuildAddCellMenuItems();
        if (this.projectId) {
            this.searchService.getIndexingNotifications(this.projectId).pipe(
                takeUntil(this.destroy$),
                startWith(null),
                switchMap(() => this.getNames$())
            ).subscribe(({ screenshots, videos }) => {
                this.screenshotsNames = screenshots;
                this.videoNames = videos;
            });
        }

        if (this.findingId && !this.finding) {
            this.update();
        } else {
            this.tmpFindingTitle = this.finding?.title || 'No title';
        }

        this.getNames$().subscribe(({ screenshots, videos }) => {
            this.screenshotsNames = screenshots;
            this.videoNames = videos;
        });

    }

    ngOnChanges(changes: SimpleChanges) {
        if (!changes.finding && !changes.findingId) {
            return;
        }

        if (!(changes.finding && _.isEqual(changes.finding.previousValue, changes.finding.currentValue)) ||
            !(changes.findingId && changes.findingId.currentValue !== changes.findingId.previousValue)) {
            this.finding = changes.finding?.currentValue;
            this.tmpFindingTitle = this.finding?.title || 'No title';
            this.findingId = changes.findingId?.currentValue;
            this.destroy$.emit();
            this.update();
        }
    }

    update() {
        if (!this.findingId) {
            return;
        }
        this.testService.getFinding$(this.findingId, true).pipe(takeUntil(this.destroy$)).subscribe(finding => {
            this.finding = finding;
            this.tmpFindingTitle = this.finding.title || 'No title';
            this.cdRef.markForCheck();
        });
        this.getNames$().subscribe(({ screenshots, videos }) => {
            this.screenshotsNames = screenshots;
            this.videoNames = videos;
        });
        this.processingFindingAction = false;
        this.uploadingAttachments = false;
    }

    getNames$(): Observable<{
        screenshots: Record<ScreenshotId, string>;
        videos: Record<VideoRecordingId, string>;
    }> {
        const screenshotObservables: Observable<{ id: ScreenshotId; name: string }>[] = [];
        const videoObservables: Observable<{ id: VideoRecordingId; name: string }>[] = [];
        if (!this.finding) {
            return of({screenshots: {}, videos: {}});
        }

        this.finding.cellSequence.forEach(cellId => {
            const cell = this.finding.cells[cellId];

            if (cell.type === "ObservationContentCell") {
                switch (cell.observation.type) {
                    case "ScreenshotObservation": {
                        const screenshotId: ScreenshotId = cell.observation.screenshotId;
                        screenshotObservables.push(
                            this.imageService.getImageMetadata(screenshotId).pipe(
                                map(metadata => ({ id: screenshotId, name: metadata.name }))
                            )
                        );
                        break;
                    }
                    case "VideoSnippetObservation": {
                        const videoId: VideoRecordingId = cell.observation.videoId;
                        videoObservables.push(
                            this.videoRecordingService.getVideoRecording(videoId).pipe(
                                map(video => ({ id: videoId, name: video.metadata.name }))
                            )
                        );
                        break;
                    }
                }
            }
        });

        return forkJoin({
            screenshots: screenshotObservables.length ? forkJoin(screenshotObservables) : of([]),
            videos: videoObservables.length ? forkJoin(videoObservables) : of([])
        }).pipe(
            map(({ screenshots, videos }) => ({
                screenshots: screenshots.reduce<Record<ScreenshotId, string>>(
                    (acc, s) => ({ ...acc, [s.id]: s.name }),
                    {}
                ),
                videos: videos.reduce<Record<VideoRecordingId, string>>(
                    (acc, v) => ({ ...acc, [v.id]: v.name }),
                    {}
                ),
            }))
        );
    }

    get hasActiveConsole(): boolean {
        return this.isWorkbenchContext && !!this.contextService.getCurrentWorkbenchContext()?.deviceContext?.activeConsole?.consoleSession;
    }

    get hasSelectedActiveConsole(): boolean {
        if (!this.isWorkbenchContext || !this.hasActiveConsole || !this.selectedCenterTabId) {
            return false;
        }
        const selectedView = this.getSelectedCenterTabView();
        return selectedView?.pluginId === 'console' && this.activeConsole?.consoleViewId === this.selectedCenterTabId;
    }

    private getSelectedCenterTabView(): WorkbenchViewConfig<any> | undefined {
        if (!this.layoutManager || !this.selectedCenterTabId) {
            return undefined;
        }
        return this.layoutManager.registeredViews[this.selectedCenterTabId];
    }

    private getSelectedConsoleDeviceId(): DeviceId | undefined {
        if (!this.hasSelectedActiveConsole) {
            return undefined;
        }
        return this.getSelectedCenterTabView()?.payload?.data?.deviceId as DeviceId | undefined;
    }

    private rebuildAddCellMenuItems() {
        const all: { type: CellType; label: string; icon: IconDefinition; disabled: boolean }[] = [
            {type: 'text',         label: 'Text',       icon: this.faAlignRight, disabled: false},
            {type: 'screenshot',   label: this.isWorkbenchContext ? 'Screenshot' : 'Image',          icon: this.faImage, disabled: this.isWorkbenchContext && !this.hasSelectedActiveConsole},
            {type: 'videoSnippet', label: this.isWorkbenchContext ? 'Screen Recording' : 'Video',  icon: this.faVideo, disabled: this.isWorkbenchContext && !this.hasSelectedActiveConsole},
            {type: 'attachment',   label: 'File',       icon: this.faFile,       disabled: false},
            {type: 'email',        label: 'E-Mail',     icon: this.faEnvelope,   disabled: !this.isWorkbenchContext},
        ];
        this.addCellMenuItems = all
            .filter(o => !o.disabled)
            .map(o => ({
                label: o.label,
                state: {icon: o.icon, type: o.type},
                command: () => {
                    this.addCellAtIndex(o.type, this.insertAtIndex);
                    this.addCellPopover.hide();
                }
            }));
    }

    onInlineAddCellClick(event: Event, index: number): void {
        this.insertAtIndex = index;
        this.addCellPopover.toggle(event);
    }

    onBottomAddCellClick(event: Event): void {
        this.insertAtIndex = this.finding?.cellSequence.length || 0;
        this.addCellPopover.toggle(event);
    }

    toggleMeta() {
        this.metaCollapsed = !this.metaCollapsed;
    }

    public toggleFinding() {
        if (!this.collapsable) {
            this.findingCollapsed = false;
        } else {
            this.findingCollapsed = !this.findingCollapsed;
        }
    }

    updateSeverity(newSeverity: FindingSeverity) {
        this.testService.updateFinding(this.finding.id, {
            severity: newSeverity,
        }).subscribe({
            next: () => {
                this.findingUpdated.emit({...this.finding, severity: newSeverity});
            },
            error: err => {
                console.error('Error updating finding severity', err);
            }
        });
    }

    refresh() {
        this.update();
    }

    deleteFinding() {
        this.processingFindingAction = true;
        this.testService.deleteFinding(this.finding.id).subscribe();
    }

    updateTitle() {
        if (!this.finding) {
            return;
        }
        this.testService.updateFinding(this.finding.id, {
            title: this.tmpFindingTitle,
        }).subscribe({
            next: () => {
                this.findingUpdated.emit({...this.finding, title: this.tmpFindingTitle});
            },
            error: err => {
                console.error('Error updating finding', err);
            }
        });
    }

    addSpecsToFinding(specs: SpecId[]) {
        this.addSpecPopover.hide();

        if (!this.finding) {
            console.warn('No active finding');
            return;
        }
        this.testService.updateFinding(this.finding.id, {
            // add specs to already associated specs, then remove duplicates by converting into a set and back
            affectedSpecs: Array.from(new Set(this.finding.affectedSpecs.concat(specs))),
        }).subscribe(() => {
            this.findingUpdated.emit(this.finding);
        });
    }

    editContentCell(cellId: ContentCellId) {
        this.editedCell = cellId;
        const cell = this.editedCell && this.finding.cells[this.editedCell];
        if (!cell) {
            console.warn('No edited cell');
            return;
        }

        const cellType = cell.type;

        if (cellType === 'TextContentCell') {
            const textCell = cell as { text: string, type: 'TextContentCell' };
            this.tmpCellEditContent = textCell.text;
        } else {
            console.warn('Unknown cell type', cellType);
            return;
        }
    }

    saveCellEdit() {
        const cell = this.editedCell && this.finding.cells[this.editedCell];
        if (!cell) {
            console.warn('No edited cell');
            return;
        }

        const cellType = cell.type;

        switch (cellType) {
            case 'TextContentCell': {
                const textCell = cell as { text: string, type: 'TextContentCell' };
                textCell.text = (this.tmpCellEditContent || '')
                    .replace(/&nbsp;/g, ' ')
                    .replace(/\u00a0/g, ' ');
                this.testService.updateFindingCell(this.finding.id, this.editedCell!, textCell).subscribe({
                    next: () => {
                        this.findingUpdated.emit({...this.finding, cells: {...this.finding.cells, [this.editedCell!]: textCell}});
                        this.editedCell = undefined;
                        this.tmpCellEditContent = '';
                    },
                    error: err => {
                        // TODO
                        console.error('Error updating finding', err);
                    }
                });
                break;
            }
            case 'ObservationContentCell': {
                const type = cell.observation.type;
                switch (type) {
                    case 'ScreenshotObservation': {
                        const id = cell.observation.screenshotId;
                        this.imageService.setImageName(id, this.screenshotsNames[id]).pipe(untilDestroyed(this),
                            switchMap(() => {
                                return this.imageService.getImageMetadata(id);
                            }),
                            tap(metadata => {
                                this.screenshotsNames[id] = metadata.name;
                            })
                        ).subscribe();
                        break;
                    }
                    case 'VideoSnippetObservation': {
                        const id = cell.observation.videoId;
                        this.videoRecordingService.setVideoName(id, this.videoNames[id]).pipe(untilDestroyed(this),
                            switchMap(() => {
                                return this.videoRecordingService.getVideoRecording(id);
                            }),
                            tap(video => {
                                this.videoNames[id] = video.metadata.name;
                            })
                        ).subscribe();
                        break;
                    }
                    case 'AttachmentObservation': {
                        break;
                    }
                }
                break;
            }
            default: {
                console.warn('Unknown cell type', cellType);
                return;
            }
        }
        this.stopContentCellEditing();
    }

    deleteCell() {
        this.testService.deleteFindingCell(this.finding.id, this.editedCell!).subscribe({});
        if (this.videoNames[this.editedCell]) {
            delete this.videoNames[this.editedCell];
        }
        if (this.screenshotsNames[this.editedCell]) {
            delete this.screenshotsNames[this.editedCell];
        }
    }

    moveCellUp() {
        const currentIdx = this.finding.cellSequence.indexOf(this.editedCell!);
        if (currentIdx === 0) {
            // cannot move up
            return;
        }

        if (currentIdx === 1) {
            // move to first position, i.e. no predecessor
            this.testService.moveFindingCell(this.finding.id, this.editedCell!, undefined).subscribe();
            return;
        } else {
            // there are at least two cells before the current one
            const prepredecessor = this.finding.cellSequence[currentIdx - 2];
            this.testService.moveFindingCell(this.finding.id, this.editedCell!, prepredecessor).subscribe();
        }
    }

    moveCellDown() {
        const currentIdx = this.finding.cellSequence.indexOf(this.editedCell!);
        if (currentIdx === this.finding.cellSequence.length - 1) {
            // cannot move down
            return;
        }

        const successor = this.finding.cellSequence[currentIdx + 1];
        this.testService.moveFindingCell(this.finding.id, this.editedCell!, successor).subscribe();
    }

    stopContentCellEditing() {
        this.editedCell = undefined;
    }

    openAttachmentFileChooser() {
        if (this.processingFindingAction || this.uploadingAttachments) {
            return;
        }
        this.attachmentFileInput?.nativeElement.click();
    }

    onAttachmentFilesSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const files = Array.from(input.files || []);
        this.handleAttachmentFiles(files);
        if (!files.length && this.finding?.id) {
            this.findingCellInsertService.clearPendingInsert(this.finding.id);
        }
        input.value = '';
    }

    onAttachmentFilesDropped(files: File[]) {
        if (this.finding?.id) {
            this.findingCellInsertService.clearPendingInsert(this.finding.id);
        }
        this.handleAttachmentFiles(files || []);
    }

    onImageFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files?.length) {
            this.handleAttachmentFiles(Array.from(input.files));
        } else if (this.finding?.id) {
            this.findingCellInsertService.clearPendingInsert(this.finding.id);
        }
    }

    onVideoFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files?.length) {
            this.handleAttachmentFiles(Array.from(input.files));
        } else if (this.finding?.id) {
            this.findingCellInsertService.clearPendingInsert(this.finding.id);
        }
    }

    private handleAttachmentFiles(files: File[]) {
        if (!files.length || !this.finding || this.uploadingAttachments || this.processingFindingAction) {
            return;
        }

        const pendingInsert = this.findingCellInsertService.consumePendingInsert(this.finding.id);

        const oversizedFiles = files.filter(file => file.size > FindingFindingComponent.MAX_ATTACHMENT_SIZE_BYTES);
        if (oversizedFiles.length > 0) {
            const maxSizeMb = this.formatMegabytes(FindingFindingComponent.MAX_ATTACHMENT_SIZE_BYTES);
            const rejectedFiles = oversizedFiles.map(file => file.name).join(', ');
            this.notificationService.toastErrorNotification(`These files exceed the ${maxSizeMb} MB limit: ${rejectedFiles}`);
        }

        const acceptedFiles = files.filter(file => file.size <= FindingFindingComponent.MAX_ATTACHMENT_SIZE_BYTES);
        if (!acceptedFiles.length) {
            return;
        }

        const projectId = this.finding.projectId || this.projectId;
        if (!projectId) {
            this.notificationService.toastErrorNotification('Could not import files because no project is active.');
            return;
        }

        this.uploadingAttachments = true;
        from(acceptedFiles).pipe(
            // Isolate each file so one that fails (or times out) doesn't abort the whole batch.
            concatMap(file => this.importDroppedFile(file, projectId).pipe(
                catchError(err => {
                    console.error(`Error importing file ${file.name}`, err);
                    this.notificationService.toastErrorNotification(`Could not import ${file.name}.`);
                    return of(null);
                })
            )),
            toArray(),
            map(cells => cells.filter((cell): cell is ContentCell => !!cell)),
            switchMap(cells => {
                if (!cells.length) {
                    return of(0);
                }
                return this.testService.appendCellsToFinding(this.finding!.id, cells).pipe(
                    switchMap(() => this.moveInsertedCellsToPendingIndex(this.finding!.id, pendingInsert)),
                    map(() => cells.length)
                );
            }),
            catchError(err => {
                this.notificationService.toastErrorNotification('Importing files failed.');
                console.error('Error importing dropped files', err);
                return throwError(() => err);
            }),
            finalize(() => {
                this.uploadingAttachments = false;
            })
        ).subscribe({
            next: importedCount => {
                if (importedCount > 0) {
                    this.notificationService.toastNotification(importedCount === 1 ? 'File imported.' : `${importedCount} files imported.`);
                }
            },
            error: () => undefined
        });
    }

    private importDroppedFile(file: File, projectId: ProjectId): Observable<ContentCell> {
        switch (this.classifyDroppedFile(file)) {
            case 'screenshot':
                return this.createScreenshotCell(file, projectId);
            case 'videoSnippet':
                return this.createVideoCell(file, projectId);
            case 'attachment':
            default:
                return this.createAttachmentCell(file, projectId);
        }
    }

    private classifyDroppedFile(file: File): ImportedFindingCellType {
        if (!file.type || file.type === 'application/octet-stream') {
            return 'attachment';
        }

        if (file.type.startsWith('image/')) {
            return 'screenshot';
        }

        if (file.type.startsWith('video/')) {
            if (!SUPPORTED_VIDEO_UPLOAD_MIME_TYPES.has(file.type)) {
                return 'attachment';
            }
            return 'videoSnippet';
        }

        return 'attachment';
    }

    private createScreenshotCell(file: File, projectId: ProjectId): Observable<ContentCell> {
        const associatedTestRun = this.finding?.testRunId as any;
        return this.imageService.storeImage(file, projectId).pipe(
            mergeMap(screenshotId => this.artifactService.addArtifact(
                projectId,
                WellKnownArtifactTypes.Screenshot,
                associatedTestRun,
                {
                    screenshotId,
                    name: file.name
                }
            ).pipe(
                map(artifactId => ({
                    type: 'ObservationContentCell' as const,
                    observation: {
                        type: 'ScreenshotObservation' as const,
                        screenshotId,
                        artifactId
                    }
                }))
            ))
        );
    }

    private createVideoCell(file: File, projectId: ProjectId): Observable<ContentCell> {
        return this.videoRecordingService.storeVideo(projectId, file, file.name).pipe(
            map(videoId => ({
                type: 'ObservationContentCell' as const,
                observation: {
                    type: 'VideoSnippetObservation' as const,
                    videoId
                }
            }))
        );
    }

    private createAttachmentCell(file: File, projectId: ProjectId): Observable<ContentCell> {
        return this.assetAndBlobService.putBlob(file, projectId).pipe(
            map(blobId => ({
                type: 'ObservationContentCell' as const,
                observation: {
                    type: 'AttachmentObservation' as const,
                    blobId,
                    name: file.name,
                    mimeType: file.type || undefined,
                    sizeBytes: file.size
                }
            }))
        );
    }

    downloadAttachment(blobId: string, fileName: string) {
        this.assetAndBlobService.downloadBlob(blobId, fileName);
    }

    getAttachmentName(cell: ContentCell): string {
        if (cell.type !== 'ObservationContentCell' || cell.observation.type !== 'AttachmentObservation') {
            return 'Attachment';
        }
        return cell.observation.name || 'Attachment';
    }

    getAttachmentSizeLabel(cell: ContentCell): string | undefined {
        if (cell.type !== 'ObservationContentCell' || cell.observation.type !== 'AttachmentObservation') {
            return undefined;
        }
        const sizeBytes = cell.observation.sizeBytes;
        if (!sizeBytes && sizeBytes !== 0) {
            return undefined;
        }
        return this.formatFileSize(sizeBytes);
    }

    private formatFileSize(sizeBytes: number): string {
        if (sizeBytes < 1024) {
            return `${sizeBytes} B`;
        }
        if (sizeBytes < 1024 * 1024) {
            return `${(sizeBytes / 1024).toFixed(1)} KB`;
        }
        return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
    }

    private formatMegabytes(sizeBytes: number): string {
        return (sizeBytes / (1024 * 1024)).toFixed(0);
    }

    private moveInsertedCellsToPendingIndex(findingId: FindingId, pendingInsert?: PendingFindingCellInsert): Observable<void> {
        if (!this.findingCellInsertService.shouldMoveCells(pendingInsert)) {
            return of(undefined);
        }

        const originalSet = new Set(pendingInsert.originalSequence);
        return this.testService.getFinding$(findingId).pipe(
            takeUntil(this.destroy$),
            map(updatedFinding => ({
                updatedFinding,
                newCellIds: updatedFinding.cellSequence.filter(id => !originalSet.has(id))
            })),
            filter(({ newCellIds }) => newCellIds.length > 0),
            take(1),
            switchMap(({ updatedFinding, newCellIds }) => {
                let predecessorId: ContentCellId | undefined = pendingInsert.insertAtIndex === 0
                    ? undefined
                    : pendingInsert.originalSequence[pendingInsert.insertAtIndex - 1];
                return from(newCellIds).pipe(
                    concatMap(cellId => {
                        const pred = predecessorId;
                        predecessorId = cellId;
                        return this.testService.moveFindingCell(updatedFinding.id, cellId, pred);
                    }),
                    toArray(),
                    map(() => undefined)
                );
            })
        );
    }

    addCellAtIndex(tpe: CellType, insertAtIndex: number) {
        if (tpe === 'text') {
            const newCell: ContentCell = {
                type: 'TextContentCell',
                text: '',
            };

            const oldCellIds = Object.keys(this.finding.cells);
            const originalSequence = [...this.finding.cellSequence];

            this.testService.appendCellToFinding(this.finding.id, newCell).subscribe({
                next: () => {
                    // Retrieve the latest finding, optionally move the new cell, then enter edit mode
                    this.testService.getFinding$(this.finding.id)
                        .pipe(
                            takeUntil(this.destroy$),
                            map(finding => {
                                const updatedCellIds = Object.keys(finding.cells);
                                const addedCellId = updatedCellIds.find(id => !oldCellIds.includes(id));
                                return { finding, addedCellId };
                            }),
                            takeWhile(({ addedCellId }) => !addedCellId, true)
                        )
                        .subscribe(({ finding, addedCellId }) => {
                            this.finding = finding;
                            this.cdRef.markForCheck();
                            if (addedCellId) {
                                if (insertAtIndex < originalSequence.length) {
                                    const predecessorId = insertAtIndex === 0
                                        ? undefined
                                        : originalSequence[insertAtIndex - 1];
                                    this.testService.moveFindingCell(this.finding.id, addedCellId, predecessorId).subscribe();
                                }
                                this.editContentCell(addedCellId);
                            }
                        });
                },
                error: err => {
                    console.error('Error updating finding', err);
                }
            });
        } else if (tpe === 'screenshot') {
            if (this.isWorkbenchContext) {
                const selectedConsoleDeviceId = this.getSelectedConsoleDeviceId();
                if (!selectedConsoleDeviceId) {
                    return;
                }
                this.findingCellInsertService.registerPendingInsert(this.finding.id, insertAtIndex, this.finding.cellSequence);
                this.workbenchConsoleService.raiseWorkbenchConsoleEvent({type: 'toolbar:openScreenshot', payload: {findingId: this.finding.id}, targetDevice: selectedConsoleDeviceId});
            } else {
                this.findingCellInsertService.registerPendingInsert(this.finding.id, insertAtIndex, this.finding.cellSequence);
                this.imageFileInput.nativeElement.value = '';
                this.imageFileInput.nativeElement.click();
            }
        } else if (tpe === 'videoSnippet') {
            if (this.isWorkbenchContext) {
                const selectedConsoleDeviceId = this.getSelectedConsoleDeviceId();
                if (!selectedConsoleDeviceId) {
                    return;
                }
                this.findingCellInsertService.registerPendingInsert(this.finding.id, insertAtIndex, this.finding.cellSequence);
                this.workbenchConsoleService.raiseWorkbenchConsoleEvent({type: 'toolbar:openVideo', payload: {findingId: this.finding.id}, targetDevice: selectedConsoleDeviceId});
            } else {
                this.findingCellInsertService.registerPendingInsert(this.finding.id, insertAtIndex, this.finding.cellSequence);
                this.videoFileInput.nativeElement.value = '';
                this.videoFileInput.nativeElement.click();
            }
        } else if (tpe === 'attachment') {
            this.findingCellInsertService.registerPendingInsert(this.finding.id, insertAtIndex, this.finding.cellSequence);
            this.openAttachmentFileChooser();
        } else if (tpe === 'email') {
            this.findingCellInsertService.registerPendingInsert(this.finding.id, insertAtIndex, this.finding.cellSequence);
            this.workbenchCommandRegistry.executeCommand('mail:toggleViewContainer');
        } else {
            console.warn('Unknown cell type', tpe);
            return;
        }
    }

    /**
     * If in workbench Context: Captures a screenshot from the active console's canvas, and then adds it as a screenshot artifact to
     * the findings, and returns an Observable<boolean> indicating success (true) or failure (false) of the operation.
     */
    addLiveScreenshotToFindings(): Observable<boolean> {

        if (!this.isWorkbenchContext && !this.activeConsole?.consoleViewId) {
            console.error('No active console');
            return of(false);
        }
        const consoleView = this.activeConsole.consoleViewId;
        const consoleDomElem = this.layoutManager.getViewDomElement(consoleView);
        if (!consoleDomElem) {
            console.error('No console dom element found');
            return of(false);
        }
        const canvasElement = $(consoleDomElem)?.find('canvas')?.[0] as HTMLCanvasElement;
        if (!canvasElement) {
            console.error('No canvas element found');
            return of(false);
        }

        // Generating screenshot from canvas.
        const imageUrl = canvasElement.toDataURL('png');
        const screenshotName = 'My Canvas Screenshot';

        return of({imageUrl, screenshotName}).pipe(
            switchMap(({imageUrl, screenshotName}) => {
                const associatedTestRun = this.finding?.testRunId as any;
                // Saving Screenshot
                return this.imageService.storeImageFromUrl(imageUrl, screenshotName, this.projectId).pipe(
                    mergeMap(screenshotId =>
                        this.artifactService.addArtifact(
                            this.projectId,
                            WellKnownArtifactTypes.Screenshot,
                            associatedTestRun,
                            {screenshotId, name: screenshotName}
                        ).pipe(
                            map(artifactId => ({screenshotId, artifactId}))
                        )
                    ),
                    switchMap(({screenshotId, artifactId}) =>
                        this.testService.appendCellToFinding(this.finding.id, {
                            type: 'ObservationContentCell',
                            observation: {
                                type: 'ScreenshotObservation',
                                screenshotId,
                                artifactId,
                            }
                        })
                    ),
                    map(() => true),
                    catchError(e => {
                        console.warn(`Could not add screenshot: ${e}`);
                        return of(false);
                    })
                );
            }),
            catchError(e => {
                console.warn(`Error: ${e}`);
                return of(false);
            })
        );
    }

    openVideo(videoId: VideoRecordingId) {
        if (this.isWorkbenchContext) {
            this.videoRecordingService.getVideoRecording(videoId).pipe(take(1)).subscribe(videoRecording => {
                this.workbenchCommandRegistry.executeCommand('workbench-tools:openVideo', {
                    id: videoRecording.id,
                    name: videoRecording.metadata?.name ?? 'Unnamed Video',
                    duration: videoRecording.metadata?.duration,
                    size: videoRecording.estimatedSize,
                    format: videoRecording.fileFormat
                });
            })
        }
        if (!this.isWorkbenchContext) {
            this.videoRecordingService.getVideoRecording(videoId)
                .pipe(take(1))
                .subscribe(videoRecording => {
                    this.videoUrl = this.videoRecordingService.getVideoUrl(videoRecording.id);
                    this.toolService.openTool(VideoViewerComponent, new VideoViewerToolConfig(this.videoUrl));

                });
        }
    }

    openScreenshot(screenshotId: ScreenshotId, event?: MouseEvent) {
        if (this.isWorkbenchContext) {
            this.workbenchCommandRegistry.executeCommand('workbench-tools:openScreenshot', {
                id: screenshotId,
                name: this.screenshotsNames[screenshotId],
            });
            event?.stopPropagation();
        }
    }

    getImageUrl(screenshotId: ScreenshotId) {
        return this.imageService.getImageUrl(screenshotId, 'medium');
    }

    makeActionDropdownModel() {
        return [
            ...(this.finding?.auditId ? [
                {
                    label: 'Move to other Activity',
                    icon: this.faArrows,
                    command: () => this.moveFinding()
                },
                {
                    label: 'Remove from Audit',
                    icon: this.faTimes,
                    command: () => this.removeFinding()
                },
            ] : []),
            {
                label: 'Delete',
                icon: this.faTrash,
                textColor: 'var(--wm-danger-text)',
                bgHoverColor: 'var(--wm-btn-danger-bg-hover)',
                command: () => this.deleteFinding()
            }
        ];
    }

    openActionMenu(event: any, menu: Menu) {
        this.currentActionDropdown = this.makeActionDropdownModel();
        menu.toggle(event);
    }

    moveFinding() {
        if (!this.finding?.auditId) return;
        this.toolService.openTool(
            PlanningMatrixSelectionToolComponent,
            new PlanningMatrixToolConfig(
                'Select activity to move to', this.finding?.projectId || this.projectId, this.finding.auditId,
                true, undefined, true,
                (selectedActivities: { [specId: string]: HydratedAuditTarget[] }) => {
                    this.processingFindingAction = true;
                    const [newSpecId, targets] = Object.entries(selectedActivities)[0];
                    this.testService.updateFinding(this.finding.id || this.findingId, {
                        affectedSpecs: [newSpecId],
                        observationTargets: [targets[0].target]
                    }).pipe(take(1)).subscribe({
                        error: () => this.notificationService.toastErrorNotification('Error moving finding')
                    });
                    this.toolService.closeLastTool();
                }
            )
        );
    }

    removeFinding() {
        this.processingFindingAction = true;
        this.testService.updateFinding(this.finding?.id || this.findingId, {audit: null}).subscribe(() => {});
    }


    protected readonly faEdit = faEdit;
    protected readonly faTrash = faTrash;
    protected readonly faPlus = faPlus;
    protected readonly faAlignRight = faAlignRight;
    protected readonly faArrowDown = faArrowDown;
    protected readonly faVideo = faVideo;
    protected readonly faArrowUp = faArrowUp;
    protected readonly faSave = faSave;
    protected readonly faChevronDown = faChevronDown;
    protected readonly faImage = faImage;
    protected readonly faChevronUp = faChevronUp;
    protected readonly faTimes = faTimes;
    protected readonly faFile = faFile;
    protected readonly faSyncAlt = faSyncAlt;
    protected readonly faEllipsisV = faEllipsisV;
    protected readonly faEnvelope = faEnvelope;
    protected readonly faExternalLink = faExternalLink;
    protected readonly faDownload = faDownload;
    protected readonly faArrows = faArrows;

    protected readonly JSON = JSON;
}
